/* eslint-disable no-unused-vars */

import React, { useEffect, useState } from "react";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Paper,
  Grid,
  FormControl,
  Button,
  MenuItem,
  Select,
  InputLabel,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableFooter
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';

import BasicSelector from "../../common/Selector";
import LocalDateSelector from "../../common/LocalDateSelector";
import PageTitle from "../../common/PageTitle";
import Loader from "../../common/Loader";
import EmptyTable from "../../common/EmptyTable";

import { useDispatch } from "react-redux";
import { deleteAPI, getAPI, getExportAPI } from "../../network";
import { checkAuthority, tableHeader, tableData, formatDatePost } from "../../utils";
import { BASE_URL } from "../../../constant";

import "./style.css";

export default function TaskList() {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();

  let companyId = localStorage.getItem('companyIdLS');
  if (!companyId) companyId = undefined;

  const [deleteId, setDeleteId] = useState('');
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const perPages = [5, 10, 25, 50];

  const [filter, setFilter] = useState({
    siteId: '',
    startDate: null,
    endDate: null,
    status: null
  });

  const [permissionList, setPermissionList] = useState([]);
  const userType = localStorage.getItem('userType');

  const actionPermissions = permissionList[0]?.actionPermissions || [];
  const canView = actionPermissions.some(p => p.action === "View");
  const canEdit = actionPermissions.some(p => p.action === "Edit");
  const showBothButtons = actionPermissions.length === 0;

  // 🚀 Load initial data
  useEffect(() => {
    getTasks();
    getSites();
    const permissions = JSON.parse(localStorage.getItem("permisionlist")) || [];
    setPermissionList(permissions);
  }, []);
  
const getTasks = async () => {
  setLoader(true);
  const url = companyId ? `?companyId=${companyId}` : '';
  const data = await getAPI(`/tasks${url}`);
  if (data) {
    // Only keep one task per group
    const groupedTasks = data.map(group => {
      const oneTask = group.tasks?.[0]; // pick the first task
      return { ...group, tasks: [oneTask] };
    });
    setTasks(groupedTasks);
  }
  setLoader(false);
};


  const getSites = async () => {
    setLoader(true);
    const data = await getAPI(`/sites?companyId=${companyId}`);
    if (data) {
      const formatted = data.map(obj => ({
        ...obj,
        label: obj.name,
        value: obj._id
      }));
      setSites(formatted);
    }
    setLoader(false);
  };

  const handleDelete = async () => {
    setLoader(true);
    await deleteAPI(`/tasks/${deleteId}`);
    setOpen(false);
    getTasks();
    setLoader(false);
  };

  const handleClearFilters = () => {
    setFilter({
      siteId: '',
      startDate: null,
      endDate: null,
      status: null
    });
    setSearch('');
    setStatus('');
  };

  const handleExport = async () => {
    setLoader(true);
    const { siteId, startDate, endDate, status: taskStatus } = filter;
    let url = '';
    let start = true;

    if (siteId) {
      url += (start ? '?' : '&') + `siteId=${siteId}`;
      start = false;
    }
    if (companyId) {
      url += (start ? '?' : '&') + `companyId=${companyId}`;
      start = false;
    }
    if (startDate) {
      url += (start ? '?' : '&') + `startDate=${formatDatePost(startDate)}`;
      start = false;
    }
    if (endDate) {
      url += (start ? '?' : '&') + `endDate=${formatDatePost(endDate)}`;
      start = false;
    }
    if (taskStatus) {
      url += (start ? '?' : '&') + `status=${taskStatus}`;
      start = false;
    }
    if (search) {
      url += (start ? '?' : '&') + `search=${search}`;
    }

    await getExportAPI(`${BASE_URL}/export/file/alltasks${url}`, `Task Lists`);
    setLoader(false);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 🔍 Apply frontend-side filters
  const filteredTasks = tasks
    .flatMap(t => t.tasks)
    .filter(task => {
      const matchSite = !filter.siteId || (Array.isArray(task.siteId)
        ? task.siteId.some(site => site._id === filter.siteId)
        : task.siteId?._id === filter.siteId);
      const matchStatus = !filter.status || task.status === filter.status;
      const matchStart = !filter.startDate || new Date(task.startDate) >= new Date(filter.startDate);
      const matchEnd = !filter.endDate || new Date(task.endDate) <= new Date(filter.endDate);
      const matchSearch = !search || String(task.workOrderNo || '').includes(search);
      return matchSite && matchStatus && matchStart && matchEnd && matchSearch;
    })
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate)); // Sort by endDate descending


  return (
    <Box>
      <Loader loader={loader} />
      <PageTitle title="Task List" />

      {/* 🔧 Filters */}
      <Box mt={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <BasicSelector
              disableAll={true}
              options={sites}
              value={filter.siteId}
              name={"Site"}
              selectorWidth="100%"
              onChange={(e) => setFilter({ ...filter, siteId: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setFilter({ ...filter, status: e.target.value });
                }}
                label="Status"
              >
                <MenuItem value="opened">Opened</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="deferred">Deferred</MenuItem>
                <MenuItem value="hold">Hold</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search by Work Order #"
              value={search}
              inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setSearch(value);
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <LocalDateSelector
              title="Start Date"
              value={filter.startDate}
              onChange={(date) => setFilter({ ...filter, startDate: date })}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <LocalDateSelector
              title="End Date"
              value={filter.endDate}
              onChange={(date) => setFilter({ ...filter, endDate: date })}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={handleExport}
              startIcon={<GetAppRoundedIcon />}
              fullWidth
              sx={{ height: '56px', backgroundColor: '#75859D' }}
            >
              Export
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              fullWidth
              sx={{ height: '56px' }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 📋 Task Table */}
      <Box mt={5}>
        <TableContainer component={Paper}>
          <Table className="responsive-table">
            <TableHead>
              <TableRow className="table-header">
                {['Title', 'Site', 'Start Date', 'End Date', 'Due Time', 'Description', 'Work Order #', 'View', 'Edit'].map(header => (
                  <TableCell key={header} align="center" sx={tableHeader}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.slice(page * perPage, page * perPage + perPage).map((task, index) => (
                <TableRow key={index}>
                  <TableCell align="center" sx={tableData}>{task?.title}</TableCell>
                  <TableCell align="center" sx={tableData}>
                    {Array.isArray(task.siteId)
                      ? task.siteId.map((s, i) => <div key={i}>{s.name}</div>)
                      : task.siteId?.name}
                  </TableCell>
                  <TableCell align="center" sx={tableData}>
                    {new Date(task.startDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center" sx={tableData}>
                    {new Date(task.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center" sx={tableData}>{task.timeDue}</TableCell>
                  <TableCell align="center" sx={{ ...tableData, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {task.description}
                  </TableCell>
                  <TableCell align="center" sx={tableData}>{task.workOrderNo}</TableCell>
                  <TableCell align="center">
                    {(showBothButtons || canView) && checkAuthority('VIEW_TASKS') && (
                      <Button
                        variant="outlined"
                        onClick={() => navigateTo(
                          userType === 'admin' ? '/tasks/details' : '/company/tasks/details',
                          { state: task.groupId }
                        )}
                      >
                        View
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {(showBothButtons || canEdit) && checkAuthority('EDIT_TASK') && (
                      <Button
                        variant="outlined"
                        onClick={() => navigateTo('/tasks/create', { state: { action: 'edit', task } })}
                      >
                        <EditIcon />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredTasks.length === 0 && <EmptyTable colSpan={9} />}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={perPages}
                  colSpan={9}
                  count={filteredTasks.length}
                  rowsPerPage={perPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* ❌ Delete Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle textAlign="center">Delete Task</DialogTitle>
        <DialogContent>
          <h3>Do you want to delete this task?</h3>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
          <Button onClick={() => setOpen(false)} variant="outlined">Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
